import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, lastValueFrom, Subscription } from 'rxjs';
import { map, share, switchMap, tap, filter, take } from 'rxjs/operators';

import { ChipModel } from '../../../Common/Forms/models/chip.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { JournodexApiService } from '../../../api/journodex/journodex.api.service';
import { JournodexSubjectApiModel } from '../../../api/journodex/model/journodex-subject-api.model';

import { JournodexSubjectModel, JournodexSubjectParentModel } from '../models/journodex.subject.model';


@Injectable()
export class JournodexSubjectsService implements OnDestroy {
    // TODO : cache in localstorage?
    private childIdToParentMap = new Map<number, JournodexSubjectParentModel>();
    private allSubjects: JournodexSubjectParentModel[];
    private allSubjects$: BehaviorSubject<JournodexSubjectParentModel[]> = new BehaviorSubject<JournodexSubjectParentModel[]>(null);
    private allSubjectsApiCall$: Observable<JournodexSubjectParentModel[]>;
    private locale: string;
    private subscriptions: Subscription[] = [];


    constructor(
        private journodexApiService: JournodexApiService,
        private currentUserStateService: CurrentUserStateService
    ) {
        // the getAllSubjects uses this.locale in journodexApiService, and the odd time, it is called before the language is set
        // this will let make sure that when we call getAllSubjects language is not detault en-us
        this.allSubjectsApiCall$ = this.currentUserStateService.getDataReferenceLanguageTag().pipe(
            filter(language => !!language),
            take(1),
            tap(language => this.locale = language),
            switchMap(() => this.journodexApiService.getAllSubjects()),
            switchMap(apiSubjects => {
                return this.createParentSubjectArrayFromApi(apiSubjects, true);
            }),
            tap(subjects => {
                this.allSubjects = subjects;
                this.allSubjects$.next(this.allSubjects);
            }),
            share()
        );
    }


    /**
     * Create an observable of data matching a search query for outlets and journalists
     *
     * @param string
     * @param existingSubjects A list of parent subjects (with children) to use to remove duplicates
     */
    public quicksearch(existingSubjects: JournodexSubjectParentModel[] | number[] = [], string: string): Observable<JournodexSubjectParentModel[]> {
        return this.journodexApiService.performQuickSearchSubjects(string).pipe(
            switchMap(subjects => {
                return this.createParentSubjectArrayFromApi(subjects);
            }),
            map(subjects => {
                if (existingSubjects.length) {
                    // Remove duplicates
                    return subjects
                        .map(parent => {
                            if (Number.isInteger(existingSubjects[0])) {
                                parent = this.removeDuplicateChildren(parent, (existingSubjects as number[]));
                            } else {
                                const possibleDuplicateParent = (existingSubjects as JournodexSubjectParentModel[]).find(existingParent => {
                                    return existingParent.id === parent.id;
                                });
                                if (possibleDuplicateParent) {
                                    parent = this.removeDuplicateChildren(parent, (possibleDuplicateParent.children));
                                }
                            }
                            return parent;
                        })
                        .filter(parent => !!parent.children.length);
                } else {
                    return subjects;
                }
            })
        );
    }

    /**
     * Create an observable of data matching a search query for outlets and journalists
     *
     * @param string
     */
    public quicksearchrelated(string: string): Observable<JournodexSubjectParentModel[]> {
        return this.journodexApiService.performQuickSearchRelatedSubjects(string).pipe(
            switchMap(subjects => {
                return this.createParentSubjectArrayFromApi(subjects);
            })
        );
    }

    public getAllSubjects(): Observable<JournodexSubjectParentModel[]> {
        if (!this.allSubjects) {
            return this.allSubjectsApiCall$;
        }
        return of(this.allSubjects.slice());
    }

    /**
     * Takes a list of child ids and returns a list of parents with those children only
     * @param ids An array of JournodexSubjectModel ids
     * @returns An array of JournodexSubjectParentModels, with the given children
     */
    public getByIds(ids: number[]): Observable<JournodexSubjectParentModel[]> {
        return this.getAllSubjects().pipe(
            map(subjects => {
                const matchingParents = new Set<JournodexSubjectParentModel>();
                const oldDataParents: JournodexSubjectParentModel[] = [];
                // Get all parents
                ids?.forEach(childId => {
                    const parent = this.childIdToParentMap.get(childId);
                    if (parent) {
                        matchingParents.add(parent);
                    } else {
                        // This id must be "old" data: a parent that is singularly selected
                        const selectedParent = subjects.find(subject => subject.id === childId);
                        if (selectedParent) {
                            oldDataParents.push(selectedParent.clone());
                        }
                    }
                });
                // Filter all parent children to only match given ids
                const localParents = [...matchingParents]
                    .map(serviceParent => {
                        const localParent = serviceParent.clone();
                        localParent.children = localParent.children.filter(possibleChild => ids.includes(possibleChild.id));
                        return localParent;
                    })
                    .concat(oldDataParents);
                return localParents;
            })
        );
    }

    /**
     * Converts a list of child ids to child subjects. Returns only a list of the children
     */
    public getChildrenByIds(ids: number[]): Observable<JournodexSubjectModel[]> {
        return this.getByIds(ids).pipe(
            map(parents => SubjectUtils.getOnlyChildrenOfSubjects(parents))
        );
    }

    // TODO: remove? not used
    public subjectsAutocomplete(searchString: string): Observable<JournodexSubjectParentModel[]> {
        return this.getAllSubjects().pipe(
            map(subjects => {
                return subjects.filter(mm => mm.name.toLocaleLowerCase().indexOf(searchString.toLocaleLowerCase()) > -1);
            })
        );
    }

    // TODO: How to make more efficient?
    public async createParentSubjectArrayFromApi(subjects: JournodexSubjectApiModel[], addToMap: boolean = false): Promise<JournodexSubjectParentModel[]> {
        interface ParentAndChildSubject {
            parent: JournodexSubjectParentModel;
            apiChildren: JournodexSubjectApiModel[];
        }

        if (!subjects || subjects.length === 0) {
            return [];
        }

        const parents = new Map<number, ParentAndChildSubject>();
        // Instantiate parents
        subjects.forEach(subject => {
            if (!subject.parent) {
                parents.set(subject.id, {
                    parent: new JournodexSubjectParentModel(subject, this.locale),
                    apiChildren: []
                });
            } else {
                const parent = this.getTopLevelParent(subject);
                if (!parents.has(parent.id)) {
                    parents.set(parent.id, {
                        parent: new JournodexSubjectParentModel(parent, this.locale),
                        apiChildren: []
                    });
                }
            }
        });
        // Retreive and Instantiate Children
        subjects.forEach(subject => {
            if (!!subject.parent) {
                const topLevelParent = this.getTopLevelParent(subject);
                const parent = parents.get(topLevelParent.id);
                parent.apiChildren.push(subject);
                if (addToMap) {
                    this.childIdToParentMap.set(subject.id, parent.parent);
                }
            }
        });

        const parentArray = [];
        for (const { parent, apiChildren } of parents.values()) {
            if (apiChildren.length === 0) {
                // This method can be called with an array of values including only parent values (without their children)
                // We have to go and fetch all the subjects to map over if this is the case
                const allSubjects = await lastValueFrom(this.getAllSubjects());
                const cachedParent = allSubjects.find(subject => subject.id === parent.id);
                parent.children = cachedParent.children.slice();
            } else {
                parent.setChildren(apiChildren, this.locale);
            }
            parentArray.push(parent);
        }
        return parentArray;
    }

    /**
     * Returns a list of given parents, with children populated from the cached subjects.
     * Equivalent to returning copies of the cached parents.
     * @param parents A list of parents (as subjects or ids) to retrieve all cached children for
     */
    public getAllCachedChildrenOfParents(parents: JournodexSubjectParentModel[] | number[]): Observable<JournodexSubjectParentModel[]> {
        const parentIds = SubjectUtils.getIdsOfSubjects(parents);
        return this.getAllSubjects().pipe(
            map(allSubjects => {
                return parentIds.map(givenParentId => {
                    return allSubjects.find(subject => subject.id === givenParentId).clone();
                });
            })
        );
    }

    private getTopLevelParent(child: JournodexSubjectApiModel): JournodexSubjectApiModel {
        let parent = child.parent;
        while (parent.parent) {
            parent = parent.parent;
        }
        return parent;
    }

    private removeDuplicateChildren(parent: JournodexSubjectParentModel, childrenToFilter: JournodexSubjectModel[] | number[]): JournodexSubjectParentModel {
        const childIds = SubjectUtils.getIdsOfSubjects(childrenToFilter);
        const nonDuplicateChildren = parent.children.filter(child => {
            return !childIds.find(possibleDuplicateChildId => possibleDuplicateChildId === child.id);
        });
        parent.children = nonDuplicateChildren.slice();
        return parent;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}

export namespace SubjectUtils {
    export function convertParentSubjectArrayToApi(subjects: JournodexSubjectParentModel[]): JournodexSubjectApiModel[] {
        const flattenedSubjects: JournodexSubjectApiModel[] = [];
        subjects?.forEach(parent => {
            parent.children.forEach(child => {
                flattenedSubjects.push(child.toApiModel());
            });
        });
        return flattenedSubjects;
    }

    /**
     * Returns the subject parent labels, with the child labels as tooltips joined by ', '
     */
    export function convertSubjectsToChips(parents: JournodexSubjectParentModel[]): ChipModel[] {
        const chips: ChipModel[] = [];
        for (const parent of parents) {
            const parentChip = { label: parent.name } as ChipModel;
            parentChip.tooltip = parent.children
                .map(child => child.names[0].value)
                .join(', ');
            chips.push(parentChip);
        }
        return chips;
    }

    export function removeChildOfSubjectById(parent: JournodexSubjectApiModel, childId: number) {
        const childIndex = parent.children.findIndex(child => child.id === childId);
        if (childIndex >= 0) {
            parent.children.splice(childIndex, 1);
        }
    }

    export function getOnlyChildrenOfSubjects(subjects: JournodexSubjectParentModel[]): JournodexSubjectModel[] {
        const children: JournodexSubjectModel[] = [];
        subjects?.forEach(parent => {
            children.push(...parent.children);
        });
        return children;
    }

    export function getIdsOfSubjects(subjects: JournodexSubjectParentModel[] | JournodexSubjectModel[] | number[]) {
        let parentIds: number[] = [];
        if (subjects[0] instanceof JournodexSubjectParentModel) {
            parentIds = (subjects as JournodexSubjectParentModel[]).map(parent => parent.id);
        } else if (subjects[0] instanceof JournodexSubjectModel) {
            parentIds = (subjects as JournodexSubjectModel[]).map(parent => parent.id);
        } else {
            parentIds = (subjects as number[]);
        }
        return parentIds;
    }

    export function doesIdContainInSubjects(subjects: JournodexSubjectParentModel[], id: number): boolean {
        return !!subjects?.some(parent => {
            return (parent.id === id) || !!parent.children?.some(child => child.id === id);
        });
    }
}
